import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  BookOpen,
  Download,
  Search,
  Home,
  Package,
  ArrowLeftRight,
  Users,
  Settings,
  Shield,
  FileText,
  HelpCircle,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Info,
  QrCode,
  MapPin,
  FolderOpen,
  Calendar,
  Trash2,
  Megaphone,
  Database,
  Wrench,
  CalendarDays,
  ClipboardList
} from 'lucide-react';
import { Input } from '../components/ui/input';

const UserManualPage = () => {
  const { api, user, hasPermission, isSuperAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('intro');

  // Build searchable content index for each section
  const sectionContentMap = {
    intro: 'bienvenido sistema inventario equipos bodega control organizado registrar historial usuario contraseña administrador',
    login: 'iniciar sesión usuario contraseña ingresar microsoft sso entra id outlook teams olvidaste bloqueada correo recuperar recordar bomberazo',
    dashboard: 'pantalla principal tablero tarjetas colores disponible asignado préstamo mantenimiento falla baja gráficos pastel líneas menú lateral kpis operaciones',
    inventory: 'inventario equipos categoría ubicación total disponible buscar filtrar exportar excel detalle número serie agregar editar permisos importar',
    movements: 'movimientos salidas entradas retorno evento préstamo responsable equipo cantidad confirmar pdf responsiva bomberazo emergencia escanear código qr eliminar editar seleccionar serie falla perdido faltante ubicación origen destino contacto lugar horario cliente',
    categories: 'categorías tipos equipo crear editar eliminar organizar laptop audio video',
    locations: 'ubicaciones almacén bodega sala lugar fisico crear editar eliminar',
    responsibles: 'responsables persona encargado nombre teléfono correo registrado crear editar eliminar',
    etiquetas: 'etiquetas qr código barras generar imprimir escanear equipo unidad ubicación descargar tamaño',
    maintenance: 'mantenimiento reparación ticket prioridad alta media baja resolver descripción asignar técnico historial preventivo correctivo',
    reservations: 'reservas reservar equipo fecha hora devolver cancelar aprobar rechazar',
    scheduled_reports: 'reportes programados automáticos email frecuencia diario semanal mensual destinatario formato excel pdf',
    templates: 'plantillas importar masivo excel csv equipos categorías',
    tracking: 'rastreo seguimiento incidentes fallas perdidos historial equipo movimiento',
    calendar: 'calendario eventos fecha programar vista mes semana día',
    trash: 'papelera eliminados recuperar restaurar borrar permanente',
    announcements: 'anuncios comunicados mensajes notificaciones crear publicar',
    users: 'usuarios crear editar eliminar rol administrador supervisor auditor almacenista bloquear desbloquear contraseña',
    permissions: 'permisos roles acceso configurar habilitar deshabilitar granular sección módulo',
    backup: 'backup respaldo descargar restaurar colecciones base datos programado automático',
    settings: 'configuración visual tema modo oscuro claro idioma empresa logo escáner',
    faq: 'preguntas frecuentes ayuda soporte problema error solución'
  };

  // Map sections to the permission needed to see them (null = visible to everyone)
  const sectionPermissionMap = {
    intro: null,
    login: null,
    dashboard: null,
    inventory: 'view_inventory',
    movements: 'view_movements',
    categories: 'view_categories',
    locations: 'view_locations',
    responsibles: 'view_responsibles',
    etiquetas: 'view_qr_labels',
    maintenance: 'view_maintenance',
    reservations: 'view_reservations',
    clients: 'view_clients',
    scheduled_reports: 'view_reports',
    templates: null,
    tracking: 'view_reports',
    calendar: 'view_calendar',
    trash: 'view_trash',
    announcements: 'manage_announcements',
    users: 'manage_users',
    permissions: 'manage_permissions',
    backup: null,  // superadmin only, checked below
    settings: 'manage_settings',
    faq: null
  };

  const allSections = [
    { id: 'intro', title: 'Introducción', icon: Home },
    { id: 'login', title: 'Iniciar Sesión', icon: Users },
    { id: 'dashboard', title: 'Dashboard', icon: Home },
    { id: 'inventory', title: 'Inventario', icon: Package },
    { id: 'categories', title: 'Categorías', icon: FolderOpen },
    { id: 'locations', title: 'Ubicaciones', icon: MapPin },
    { id: 'responsibles', title: 'Responsables', icon: Users },
    { id: 'movements', title: 'Movimientos', icon: ArrowLeftRight },
    { id: 'etiquetas', title: 'Etiquetas QR', icon: QrCode },
    { id: 'maintenance', title: 'Mantenimiento', icon: Wrench },
    { id: 'reservations', title: 'Reservas', icon: CalendarDays },
    { id: 'clients', title: 'Clientes', icon: Users },
    { id: 'scheduled_reports', title: 'Reportes Programados', icon: ClipboardList },
    { id: 'templates', title: 'Plantillas', icon: FileText },
    { id: 'tracking', title: 'Rastreo', icon: Search },
    { id: 'calendar', title: 'Calendario', icon: Calendar },
    { id: 'trash', title: 'Papelera', icon: Trash2 },
    { id: 'announcements', title: 'Anuncios', icon: Megaphone },
    { id: 'users', title: 'Usuarios', icon: Users },
    { id: 'permissions', title: 'Permisos', icon: Shield },
    { id: 'backup', title: 'Backup', icon: Database },
    { id: 'settings', title: 'Configuración', icon: Settings },
    { id: 'faq', title: 'Preguntas Frecuentes', icon: HelpCircle }
  ];

  // Filter sections based on user's actual permissions
  const sections = allSections.filter(section => {
    const requiredPerm = sectionPermissionMap[section.id];
    // Superadmin sees everything
    if (isSuperAdmin()) return true;
    // Backup is superadmin only
    if (section.id === 'backup') return false;
    // No permission required = visible to all
    if (requiredPerm === null) return true;
    // Check actual permission
    return hasPermission(requiredPerm);
  });

  const handleDownloadPDF = async () => {
    try {
      const response = await api().get('/manual/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Manual_Usuario_CM_Idiomas.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading manual');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            Manual de Usuario
          </h1>
          <p className="text-muted-foreground">
            Guía completa para usar el sistema de inventario
          </p>
        </div>
        <Button onClick={handleDownloadPDF} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Descargar PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Contenido</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <ScrollArea className="h-[500px]">
              <div className="space-y-1">
                {sections.filter(s => {
                  const term = searchTerm.toLowerCase();
                  return s.title.toLowerCase().includes(term) || 
                    (sectionContentMap[s.id] || '').toLowerCase().includes(term);
                }).map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                      activeSection === section.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted'
                    }`}
                  >
                    <section.icon className="h-4 w-4" />
                    {section.title}
                    <ChevronRight className={`h-4 w-4 ml-auto transition-transform ${
                      activeSection === section.id ? 'rotate-90' : ''
                    }`} />
                  </button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Content Area */}
        <Card className="lg:col-span-3">
          <CardContent className="pt-6">
            <ScrollArea className="h-[600px] pr-4">
              {/* Introduction */}
              {activeSection === 'intro' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-4">Bienvenido al Sistema de Inventario</h2>
                    <p className="text-muted-foreground mb-4">
                      Este sistema te permite gestionar el inventario de equipos de CM Idiomas. 
                      Aquí podrás ver qué equipos hay, quién los tiene, y registrar cuando 
                      salen o regresan.
                    </p>
                    <p className="text-muted-foreground mb-4">
                      <strong>No te preocupes si no sabes mucho de computadoras.</strong> Este manual 
                      te explicará todo paso a paso con imágenes y ejemplos claros.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <h3 className="font-semibold mb-3">¿Qué puedes hacer en este sistema?</h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>✅ Ver todos los equipos que tiene la empresa</p>
                      <p>✅ Saber dónde está cada equipo (en bodega, en un evento, prestado, etc.)</p>
                      <p>✅ Registrar cuando alguien se lleva equipo</p>
                      <p>✅ Registrar cuando el equipo regresa</p>
                      <p>✅ Ver el historial de todo lo que ha pasado con cada equipo</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <CheckCircle className="h-6 w-6 text-emerald-500 mb-2" />
                      <h3 className="font-semibold mb-1">Control Total</h3>
                      <p className="text-sm text-muted-foreground">
                        Siempre sabrás exactamente dónde está cada equipo.
                      </p>
                    </div>
                    <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <Package className="h-6 w-6 text-blue-500 mb-2" />
                      <h3 className="font-semibold mb-1">Todo Organizado</h3>
                      <p className="text-sm text-muted-foreground">
                        Los equipos están ordenados por categorías y ubicaciones.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-1">Antes de Comenzar</h3>
                        <p className="text-sm text-muted-foreground">
                          Para usar el sistema necesitas un <strong>nombre de usuario</strong> y una <strong>contraseña</strong>. 
                          Si no los tienes, pide ayuda al administrador (la persona encargada del sistema).
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-3">¿Cómo usar este manual?</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      En el menú de la izquierda verás diferentes secciones. Haz clic en la que necesites:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                      <li>• <strong>Iniciar Sesión</strong> - Cómo entrar al sistema</li>
                      <li>• <strong>Dashboard</strong> - La pantalla principal</li>
                      <li>• <strong>Inventario</strong> - Ver y agregar equipos</li>
                      <li>• <strong>Movimientos</strong> - Registrar salidas y entradas</li>
                      <li>• <strong>Plantillas</strong> - Agregar muchos equipos de una vez</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Login Section */}
              {activeSection === 'login' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Cómo Iniciar Sesión (Entrar al Sistema)</h2>
                  
                  <p className="text-muted-foreground">
                    Para usar el sistema, primero debes "iniciar sesión". Esto significa 
                    identificarte con tu usuario y contraseña para que el sistema sepa quién eres.
                  </p>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Sigue estos pasos:</h3>
                    <ol className="list-decimal list-inside space-y-4 text-muted-foreground">
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 1: Abre el sistema en tu computadora</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>El administrador te dará una dirección web (será <strong>cminventario.com</strong>).</p>
                          <p>Abre tu navegador (el programa para ver internet, como Chrome, Firefox o Edge).</p>
                          <p>Escribe la dirección en la barra de arriba donde normalmente escribes las páginas web.</p>
                          <p>Presiona la tecla <strong>Enter</strong> en tu teclado.</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 2: Verás la pantalla de inicio de sesión</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>Verás el logo de CM Idiomas y dos espacios en blanco para escribir.</p>
                          <p>El primer espacio dice <strong>"Usuario"</strong>.</p>
                          <p>El segundo espacio dice <strong>"Contraseña"</strong>.</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 3: Escribe tu nombre de usuario</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>Haz clic con el ratón en el espacio que dice "Usuario".</p>
                          <p>El espacio se pondrá de un color diferente para indicar que está seleccionado.</p>
                          <p>Escribe tu nombre de usuario <strong>exactamente como te lo dieron</strong> (respeta mayúsculas y minúsculas).</p>
                          <p className="text-amber-400">⚠️ Ejemplo: si tu usuario es "maria.lopez", no escribas "Maria.Lopez".</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 4: Escribe tu contraseña</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>Haz clic en el espacio que dice "Contraseña".</p>
                          <p>Escribe tu contraseña. <strong>Verás puntos (•••) en vez de letras</strong> - esto es normal, es para proteger tu contraseña.</p>
                          <p>💡 <strong>Consejo:</strong> Si quieres ver lo que estás escribiendo, haz clic en el ícono del ojo 👁️ que está a la derecha del campo.</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 5: Opción "Recordar mi usuario"</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>Debajo de la contraseña verás una casilla que dice "Recordar mi usuario".</p>
                          <p>Si marcas esta casilla (haciendo clic en ella), la próxima vez que abras el sistema ya aparecerá tu usuario escrito.</p>
                          <p className="text-amber-400">⚠️ Solo usa esta opción en tu computadora personal, no en computadoras compartidas.</p>
                        </div>
                      </li>
                      <li className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 6: Haz clic en el botón "Ingresar"</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>Es el botón grande de color verde que está debajo.</p>
                          <p>Si todo está correcto, entrarás al sistema y verás el Dashboard (pantalla principal).</p>
                          <p className="text-emerald-400">✅ ¡Listo! Ya estás dentro del sistema.</p>
                        </div>
                      </li>
                    </ol>
                  </div>

                  {/* SSO Microsoft Section */}
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
                          <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                          <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                          <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                          <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Iniciar Sesión con Microsoft (SSO)</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Si tu empresa usa Microsoft 365 (Outlook, Teams, etc.), puedes entrar al sistema 
                          usando tu cuenta de Microsoft sin necesidad de recordar otro usuario y contraseña.
                        </p>
                        <ol className="text-sm text-muted-foreground space-y-3 list-decimal list-inside">
                          <li className="p-2 bg-background/50 rounded">
                            En la pantalla de inicio de sesión, busca el botón que dice <strong>"Iniciar sesión con Microsoft"</strong> (tiene el logo de Microsoft: 4 cuadritos de colores)
                          </li>
                          <li className="p-2 bg-background/50 rounded">
                            Haz clic en ese botón. Se abrirá una ventana de Microsoft
                          </li>
                          <li className="p-2 bg-background/50 rounded">
                            Escribe tu <strong>correo de Microsoft</strong> (el que usas para Outlook o Teams) y tu <strong>contraseña de Microsoft</strong>
                          </li>
                          <li className="p-2 bg-background/50 rounded">
                            Microsoft te preguntará <strong>"¿Mantener la sesión iniciada?"</strong> — puedes elegir "Sí" en tu computadora personal
                          </li>
                          <li className="p-2 bg-background/50 rounded">
                            Verás una pantalla que dice <strong>"Procesando inicio de sesión..."</strong> con un spinner girando
                          </li>
                          <li className="p-2 bg-emerald-500/10 rounded border border-emerald-500/20">
                            <span className="text-emerald-400">✅ ¡Listo!</span> Serás redirigido automáticamente al Dashboard
                          </li>
                        </ol>
                        <div className="mt-3 p-2 bg-background/50 rounded text-sm">
                          <p className="font-medium text-foreground mb-1">¿Es la primera vez que entras con Microsoft?</p>
                          <p className="text-muted-foreground">
                            No te preocupes. El sistema creará tu cuenta automáticamente usando tu nombre y correo de Microsoft. 
                            Tu foto de perfil de Microsoft también aparecerá automáticamente. 
                            Tu rol inicial será "usuario" y el administrador podrá cambiarlo si es necesario.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-2">¿Olvidaste tu contraseña?</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          No te preocupes, puedes recuperarla siguiendo estos pasos:
                        </p>
                        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                          <li>Debajo del botón verde, haz clic donde dice <strong>"¿Olvidaste tu contraseña?"</strong></li>
                          <li>Escribe tu correo electrónico (el mismo que usaste al registrarte)</li>
                          <li>Recibirás un correo con instrucciones para crear una nueva contraseña</li>
                          <li>Revisa también tu carpeta de "Spam" o "Correo no deseado"</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-2">⚠️ Cuenta Bloqueada</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Por seguridad, si escribes la contraseña incorrecta <strong>10 veces seguidas</strong>, 
                          el sistema bloqueará tu cuenta automáticamente.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Si esto te pasa, contacta al administrador para que desbloquee tu cuenta.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-2">Problemas comunes al iniciar sesión:</h3>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <div className="p-2 bg-muted/30 rounded">
                        <p className="font-medium text-foreground">❌ "Usuario o contraseña incorrectos"</p>
                        <p>Revisa que escribiste bien tu usuario y contraseña. Recuerda que las mayúsculas importan.</p>
                      </div>
                      <div className="p-2 bg-muted/30 rounded">
                        <p className="font-medium text-foreground">❌ "Cuenta bloqueada"</p>
                        <p>Escribiste mal la contraseña muchas veces. Contacta al administrador.</p>
                      </div>
                      <div className="p-2 bg-muted/30 rounded">
                        <p className="font-medium text-foreground">❌ La página no carga</p>
                        <p>Revisa tu conexión a internet. Si el problema continúa, contacta al administrador.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Dashboard Section */}
              {activeSection === 'dashboard' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Dashboard (Pantalla Principal)</h2>
                  
                  <p className="text-muted-foreground">
                    El Dashboard es la primera pantalla que verás después de iniciar sesión. 
                    Es como un "tablero de control" donde puedes ver de un vistazo cómo está tu inventario.
                  </p>

                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <h3 className="font-semibold mb-2">¿Qué es el Dashboard?</h3>
                    <p className="text-sm text-muted-foreground">
                      Piensa en el Dashboard como el panel de un automóvil: te muestra información 
                      importante de manera rápida sin tener que buscar en todo el sistema.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Las tarjetas de colores</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      En la parte superior verás varias tarjetas de colores. Cada una te dice cuántos 
                      equipos hay en cada estado:
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2 bg-emerald-500/20 text-emerald-400">Disponible</Badge>
                        <p className="text-sm text-muted-foreground">
                          <strong>Equipos listos para usar.</strong> Están en la bodega esperando 
                          a ser usados en un evento o prestados a alguien.
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2 bg-blue-500/20 text-blue-400">Asignado</Badge>
                        <p className="text-sm text-muted-foreground">
                          <strong>Equipos en uso.</strong> Están asignados a un evento o 
                          alguien los está usando en este momento.
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2 bg-purple-500/20 text-purple-400">En Préstamo</Badge>
                        <p className="text-sm text-muted-foreground">
                          <strong>Equipos prestados.</strong> Una persona se los llevó 
                          temporalmente y debe devolverlos.
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2 bg-amber-500/20 text-amber-400">Mantenimiento</Badge>
                        <p className="text-sm text-muted-foreground">
                          <strong>Equipos en reparación.</strong> Están siendo revisados 
                          o arreglados, no se pueden usar temporalmente.
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2 bg-red-500/20 text-red-400">Con Falla</Badge>
                        <p className="text-sm text-muted-foreground">
                          <strong>Equipos descompuestos.</strong> Tienen algún problema 
                          y necesitan ser reparados.
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2 bg-gray-500/20 text-gray-400">Dado de Baja</Badge>
                        <p className="text-sm text-muted-foreground">
                          <strong>Equipos retirados.</strong> Ya no se usan, fueron 
                          desechados o vendidos.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-2">💡 Consejo: Las tarjetas son clickeables</h3>
                        <p className="text-sm text-muted-foreground">
                          Si haces clic en cualquier tarjeta de color, el sistema te llevará 
                          directamente al inventario mostrando solo los equipos en ese estado.
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Por ejemplo: haz clic en "Disponible" para ver solo los equipos disponibles.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Los gráficos</h3>
                    <p className="text-sm text-muted-foreground">
                      Más abajo verás gráficos que muestran información visual:
                    </p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="p-3 bg-muted/30 rounded">
                        <strong>Equipos por Estado:</strong> Un gráfico circular (de pastel) que muestra 
                        qué porcentaje de equipos está en cada estado.
                      </li>
                      <li className="p-3 bg-muted/30 rounded">
                        <strong>Movimientos por Mes:</strong> Una gráfica de líneas que muestra cuántos 
                        movimientos (salidas y entradas) hubo cada mes.
                      </li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">El menú lateral (lado izquierdo)</h3>
                    <p className="text-sm text-muted-foreground">
                      A la izquierda de la pantalla verás una columna oscura con varias opciones. 
                      Este es el menú de navegación. Haz clic en cualquier opción para ir a esa sección:
                    </p>
                    <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                      <li>• <strong>Dashboard:</strong> Regresa a esta pantalla principal</li>
                      <li>• <strong>Inventario:</strong> Ver todos los equipos</li>
                      <li>• <strong>Movimientos:</strong> Registrar salidas y entradas</li>
                      <li>• <strong>Calendario:</strong> Ver eventos programados</li>
                      <li>• <strong>Reportes:</strong> Generar informes</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Inventory Section */}
              {activeSection === 'inventory' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Inventario (Ver y Agregar Equipos)</h2>
                  
                  <p className="text-muted-foreground">
                    En la sección de Inventario puedes ver todos los equipos que tiene la empresa, 
                    buscar equipos específicos, y si tienes permisos, agregar nuevos equipos.
                  </p>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Cómo llegar al Inventario</h3>
                    <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                      <li className="p-3 bg-muted/30 rounded-lg">
                        En el menú de la izquierda, busca la palabra <strong>"Inventario"</strong>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        Haz clic en ella
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        Verás una lista de todos los equipos
                      </li>
                    </ol>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Entendiendo la pantalla de Inventario</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      La pantalla muestra una lista de equipos. Cada fila es un tipo de equipo diferente:
                    </p>
                    <div className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-start gap-3">
                        <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs">Nombre</span>
                        <p className="text-sm text-muted-foreground">El nombre del equipo (ej: "MacBook Pro 14")</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs">Categoría</span>
                        <p className="text-sm text-muted-foreground">El tipo de equipo (ej: "Laptops", "Audio", "Video")</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs">Total</span>
                        <p className="text-sm text-muted-foreground">Cuántas unidades hay de ese equipo en total</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs">Disponible</span>
                        <p className="text-sm text-muted-foreground">Cuántas unidades están libres para usar</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Cómo buscar un equipo</h3>
                    <ol className="list-decimal list-inside space-y-3 text-muted-foreground">
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Encuentra la barra de búsqueda</span>
                        <p className="ml-6 mt-1">Está en la parte superior, dice "Buscar equipo..."</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Haz clic en la barra de búsqueda</span>
                        <p className="ml-6 mt-1">El cursor aparecerá dentro del campo</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Escribe lo que buscas</span>
                        <p className="ml-6 mt-1">Puedes escribir el nombre del equipo, número de serie, o marca</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">La lista se filtrará automáticamente</span>
                        <p className="ml-6 mt-1">Solo verás los equipos que coincidan con lo que escribiste</p>
                      </li>
                    </ol>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Cómo filtrar por categoría o estado</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Además de buscar, puedes filtrar los equipos usando los menús desplegables:
                    </p>
                    <ol className="list-decimal list-inside space-y-3 text-muted-foreground">
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Busca los menús desplegables</span>
                        <p className="ml-6 mt-1">Están junto a la barra de búsqueda. Dicen "Todas las categorías", "Todas las ubicaciones", etc.</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Haz clic en el menú que quieras usar</span>
                        <p className="ml-6 mt-1">Se abrirá una lista de opciones</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Selecciona la opción deseada</span>
                        <p className="ml-6 mt-1">Por ejemplo, selecciona "Laptops" para ver solo las laptops</p>
                      </li>
                    </ol>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Cómo ver los detalles de un equipo</h3>
                    <ol className="list-decimal list-inside space-y-3 text-muted-foreground">
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Encuentra el equipo en la lista</span>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Haz clic en la flechita (▼) al lado derecho</span>
                        <p className="ml-6 mt-1">Se expandirá mostrando las unidades individuales con sus números de serie</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Verás cada unidad con su información</span>
                        <p className="ml-6 mt-1">Número de serie, ubicación actual, y estado de cada unidad</p>
                      </li>
                    </ol>
                  </div>

                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-2">Exportar el inventario a Excel</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Puedes descargar todo el inventario en un archivo de Excel:
                        </p>
                        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                          <li>En la esquina superior derecha, busca el botón <strong>"Exportar"</strong></li>
                          <li>Haz clic en él</li>
                          <li>Se descargará un archivo Excel con todos los equipos</li>
                          <li>Puedes abrir este archivo con Excel o Google Sheets</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-1">Nota sobre permisos</h3>
                        <p className="text-sm text-muted-foreground">
                          No todos los usuarios pueden agregar o editar equipos. Si no ves los 
                          botones de "Nuevo Equipo" o "Editar", es porque tu cuenta no tiene 
                          esos permisos. Contacta al administrador si necesitas hacer cambios.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Movements Section */}
              {activeSection === 'movements' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Movimientos (Salidas y Entradas de Equipo)</h2>
                  
                  <p className="text-muted-foreground">
                    Aquí registras cuando alguien se lleva equipo (una "salida") y cuando el equipo 
                    regresa (una "entrada" o "retorno"). Es muy importante registrar todos los movimientos 
                    para saber dónde está cada equipo.
                  </p>

                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <h3 className="font-semibold mb-2">¿Qué es un movimiento?</h3>
                    <p className="text-sm text-muted-foreground">
                      Un "movimiento" es cualquier vez que el equipo cambia de lugar o de persona. 
                      Hay dos tipos principales:
                    </p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                      <li>• <strong>Evento:</strong> Cuando el equipo se usa en un evento de la empresa</li>
                      <li>• <strong>Préstamo:</strong> Cuando alguien se lleva equipo para uso personal</li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Cómo registrar una SALIDA de equipo</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Sigue estos pasos cuando alguien necesite llevarse equipo:
                    </p>
                    <ol className="list-decimal list-inside space-y-4 text-muted-foreground">
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 1: Ir a Movimientos</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>En el menú de la izquierda, haz clic en <strong>"Movimientos"</strong></p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 2: Clic en "Nueva Salida"</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Es un botón verde en la esquina superior derecha</p>
                          <p>Se abrirá una ventana (formulario) para llenar los datos</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 3: Selecciona el tipo de salida</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Verás dos pestañas: <strong>"Evento"</strong> y <strong>"Préstamo"</strong></p>
                          <p>Haz clic en la que corresponda:</p>
                          <p className="ml-4">• <strong>Evento:</strong> Para eventos de trabajo de la empresa</p>
                          <p className="ml-4">• <strong>Préstamo:</strong> Cuando alguien se lleva equipo para uso personal</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 4: Completa la información del evento/préstamo</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p><strong>Si es EVENTO:</strong></p>
                          <p className="ml-4">• Nombre del evento (ej: "Conferencia UNAM")</p>
                          <p className="ml-4">• Número de control (si lo tienes)</p>
                          <p className="ml-4">• Fecha del evento</p>
                          <p className="mt-2"><strong>Si es PRÉSTAMO:</strong></p>
                          <p className="ml-4">• Razón del préstamo (para qué lo necesita)</p>
                          <p className="ml-4">• Fecha de devolución esperada</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 5: Selecciona el responsable</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Haz clic en el menú desplegable de "Responsable"</p>
                          <p>Selecciona el nombre de la persona que se llevará el equipo</p>
                          <p className="text-amber-400">⚠️ El responsable debe estar registrado en el sistema</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 6: Agrega los equipos que saldrán</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>Esta es la parte más importante. Debes especificar qué equipos se llevarán:</p>
                          <ol className="list-[lower-alpha] list-inside space-y-1 ml-4">
                            <li>Selecciona la <strong>Ubicación</strong> de donde sale el equipo (ej: "Almacén")</li>
                            <li>Selecciona la <strong>Categoría</strong> del equipo (ej: "Laptops")</li>
                            <li>Selecciona el <strong>Equipo</strong> específico (ej: "MacBook Pro 14")</li>
                            <li>Escribe la <strong>Cantidad</strong> (cuántas unidades)</li>
                            <li>Haz clic en <strong>"Agregar"</strong> para añadirlo a la lista</li>
                          </ol>
                          <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <p className="font-medium text-blue-400 mb-2">💡 Selección de Números de Serie</p>
                            <p className="text-sm">Si el equipo tiene unidades con números de serie registrados, aparecerá una sección para seleccionar las unidades específicas:</p>
                            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-sm">
                              <li><strong>Buscador:</strong> Escribe para filtrar números de serie rápidamente</li>
                              <li><strong>Seleccionar todos:</strong> Marca todas las unidades visibles de una vez</li>
                              <li><strong>Limpiar:</strong> Desmarca todas las selecciones</li>
                              <li>Haz clic en cada checkbox para seleccionar unidades individuales</li>
                            </ul>
                            <p className="text-sm mt-2 text-muted-foreground">El contador te mostrará cuántas unidades has seleccionado</p>
                          </div>
                          <p className="text-emerald-400 mt-2">✅ Repite este proceso para cada tipo de equipo que saldrá</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 7: Revisa el equipo antes de la salida</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Antes de confirmar, debes revisar físicamente que el equipo está completo y funcionando</p>
                          <p>Marca la casilla que dice <strong>"Revisé el equipo"</strong></p>
                          <p className="text-amber-400 mt-2">⚠️ <strong>Emergencias (Bomberazos):</strong> Si no hay tiempo de revisar, 
                          puedes marcar "Sin revisión", pero esto quedará registrado</p>
                        </div>
                      </li>
                      <li className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 8: Confirma la salida</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Revisa que toda la información esté correcta</p>
                          <p>Haz clic en el botón <strong>"Confirmar Salida"</strong></p>
                          <p className="text-emerald-400">✅ ¡Listo! La salida quedó registrada</p>
                          <p>Se generará un PDF de responsiva que puedes imprimir</p>
                        </div>
                      </li>
                    </ol>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Cómo registrar un RETORNO de equipo</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Cuando el equipo regresa, debes registrar el retorno:
                    </p>
                    <ol className="list-decimal list-inside space-y-4 text-muted-foreground">
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 1: Ve a "Movimientos" en el menú</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>En el menú de la izquierda, busca y haz clic en <strong>"Movimientos"</strong></p>
                          <p>Verás una lista con todos los movimientos que se han registrado</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 2: Busca el movimiento que quieres cerrar</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Usa la barra de búsqueda para encontrar el movimiento (puedes buscar por nombre del evento o responsable)</p>
                          <p>Los movimientos pendientes de retorno tienen un estado que dice <strong>"Pendiente"</strong> con color amarillo/naranja</p>
                        </div>
                      </li>
                      <li className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 3: Haz clic en el ícono de "Retornar"</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>En la columna <strong>"Acciones"</strong> (última columna a la derecha), verás varios íconos pequeños:</p>
                          <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>Ojo (👁)</strong> - Ver detalles del movimiento</li>
                            <li><strong>PDF (📄)</strong> - Descargar el PDF de la responsiva</li>
                            <li><strong>Flecha circular (↩)</strong> - <span className="text-amber-400 font-medium">ESTE es el botón de RETORNO</span></li>
                            <li><strong>Lápiz (✏)</strong> - Editar el movimiento</li>
                          </ul>
                          <p className="text-amber-400 font-medium mt-2">Haz clic en la flecha circular (↩) que significa "Retornar"</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 4: Revisa físicamente el equipo</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Se abrirá una ventana donde verás todo el equipo que debe regresar</p>
                          <p><strong>Antes de confirmar</strong>, verifica físicamente:</p>
                          <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li>¿Están todos los equipos? (cuenta las cantidades)</li>
                            <li>¿Funcionan correctamente?</li>
                            <li>¿Tienen algún daño o falla?</li>
                          </ul>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 5: Selecciona el estado de cada equipo</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Para cada equipo, selecciona cómo regresó:</p>
                          <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                            <li><strong>Bueno</strong> - El equipo está bien, funciona correctamente</li>
                            <li><strong>Con Falla</strong> - El equipo tiene algún problema (describe la falla en el campo que aparece)</li>
                            <li><strong>Perdido</strong> - El equipo no regresó</li>
                          </ul>
                          <p className="mt-2">También selecciona la <strong>ubicación</strong> donde guardarás el equipo (ej: "Almacén Principal")</p>
                          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="font-medium text-red-400 mb-2">⚠️ Equipos con Falla o Perdidos - Selección de Números de Serie</p>
                            <p className="text-sm">Si marcas algún equipo como "Con Falla" o "Perdido" y tiene números de serie, deberás indicar cuál(es) unidad(es) específica(s) están afectadas:</p>
                            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-sm">
                              <li><strong>Buscador:</strong> Escribe para filtrar números de serie rápidamente</li>
                              <li><strong>Todos:</strong> Selecciona todas las unidades visibles de una vez</li>
                              <li><strong>Limpiar:</strong> Desmarca todas las selecciones</li>
                              <li><strong>No identificado:</strong> Si no sabes cuál unidad es, selecciona esta opción (tienes 5 días para verificarlo)</li>
                            </ul>
                          </div>
                        </div>
                      </li>
                      <li className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 6: Confirma el retorno</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Si quieres agregar alguna nota, escríbela en el campo "Notas del retorno"</p>
                          <p>Haz clic en el botón verde <strong>"Confirmar Retorno"</strong></p>
                          <p className="text-emerald-400 mt-2">✅ ¡Listo! El equipo vuelve a estar disponible en el inventario</p>
                          <p className="text-emerald-400">✅ El movimiento cambia de "Pendiente" a "Completado"</p>
                        </div>
                      </li>
                    </ol>
                    
                    <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <h3 className="font-semibold mb-3">📋 Otras acciones en Movimientos</h3>
                      <div className="space-y-3 text-sm">
                        <div className="p-3 bg-background/50 rounded">
                          <p className="font-medium text-foreground">👁 Ver detalles del movimiento</p>
                          <p className="text-muted-foreground mt-1">Haz clic en el ícono del ojo para ver toda la información: qué equipo salió, quién lo tiene, cuándo debe regresar, etc.</p>
                        </div>
                        <div className="p-3 bg-background/50 rounded">
                          <p className="font-medium text-foreground">📄 Generar PDF de la responsiva</p>
                          <p className="text-muted-foreground mt-1">Haz clic en el ícono de PDF para descargar el documento de responsiva. Este PDF incluye los datos del evento, equipo prestado y espacios para firmas. Lo puedes imprimir para que lo firme el responsable.</p>
                        </div>
                        <div className="p-3 bg-background/50 rounded">
                          <p className="font-medium text-foreground">✏ Editar un movimiento</p>
                          <p className="text-muted-foreground mt-1">Si cometiste un error (ej: pusiste el equipo incorrecto), haz clic en el lápiz para corregirlo. Solo los administradores pueden editar movimientos ya registrados.</p>
                        </div>
                        <div className="p-3 bg-background/50 rounded">
                          <p className="font-medium text-foreground">🗑 Eliminar un movimiento</p>
                          <p className="text-muted-foreground mt-1">Si registraste un movimiento por error, puedes eliminarlo. ¡Ten cuidado! Esta acción no se puede deshacer. Solo los administradores pueden eliminar.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-2">⚠️ ¿Qué pasa si falta equipo o está dañado?</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Si al recibir el equipo notas que falta algo o está dañado:
                        </p>
                        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                          <li>En el Paso 5, selecciona <strong>"Con Falla"</strong> o <strong>"Perdido"</strong> según corresponda</li>
                          <li>Si está dañado, describe el problema en el campo de texto que aparece</li>
                          <li>Agrega notas adicionales si es necesario</li>
                          <li>Informa al administrador inmediatamente</li>
                          <li>El equipo dañado quedará marcado en el sistema y no estará disponible hasta que se repare</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Etiquetas QR Section */}
              {activeSection === 'etiquetas' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Etiquetas QR y Códigos de Barras</h2>
                  
                  <p className="text-muted-foreground">
                    El sistema permite generar etiquetas con códigos QR o códigos de barras para identificar 
                    equipos y ubicaciones de forma rápida. Puedes imprimir estas etiquetas y pegarlas 
                    en los equipos para facilitar el registro de salidas y entradas.
                  </p>

                  <div className="bg-primary/10 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <QrCode className="h-5 w-5 text-primary" />
                      Tipos de Códigos
                    </h3>
                    <ul className="list-disc ml-6 space-y-1 text-sm">
                      <li><strong>EQ:</strong> Tipo de equipo (ej: "Laptop HP")</li>
                      <li><strong>UN:</strong> Unidad específica con número de serie</li>
                      <li><strong>LOC:</strong> Ubicación física (ej: "Sala de Juntas")</li>
                    </ul>
                  </div>

                  <div className="space-y-4 mt-6">
                    <h3 className="text-lg font-semibold">📋 Cómo Generar Etiquetas</h3>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-4 py-3 border-b">
                        <span className="font-medium text-foreground text-base">Paso 1: Ir a Etiquetas QR</span>
                      </div>
                      <div className="px-4 py-3">
                        <p>En el menú de la izquierda, haz clic en <strong>"Etiquetas QR"</strong></p>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-4 py-3 border-b">
                        <span className="font-medium text-foreground text-base">Paso 2: Seleccionar elementos</span>
                      </div>
                      <div className="px-4 py-3">
                        <ul className="list-disc ml-6 space-y-1">
                          <li>Usa las pestañas para elegir: <strong>Equipos</strong>, <strong>Unidades</strong> o <strong>Ubicaciones</strong></li>
                          <li>Marca los elementos que deseas etiquetar</li>
                          <li>Usa "Seleccionar todo" para marcar todos los elementos</li>
                        </ul>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-4 py-3 border-b">
                        <span className="font-medium text-foreground text-base">Paso 3: Elegir tipo de código</span>
                      </div>
                      <div className="px-4 py-3">
                        <p>Selecciona el tipo de código en el menú desplegable:</p>
                        <ul className="list-disc ml-6 space-y-1 mt-2">
                          <li><strong>QR Code:</strong> Cuadrado, puede escanearse con cualquier celular</li>
                          <li><strong>Código de Barras:</strong> Lineal, requiere lector de código de barras</li>
                        </ul>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-4 py-3 border-b">
                        <span className="font-medium text-foreground text-base">Paso 4: Generar e Imprimir</span>
                      </div>
                      <div className="px-4 py-3">
                        <ul className="list-disc ml-6 space-y-1">
                          <li>Haz clic en <strong>"Generar códigos"</strong></li>
                          <li>Elige el tamaño de etiqueta (pequeña, mediana o grande)</li>
                          <li>Haz clic en <strong>"Imprimir etiquetas"</strong> para imprimir</li>
                          <li>O usa <strong>"Descargar imágenes"</strong> para guardarlas</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mt-6">
                    <h3 className="text-lg font-semibold">📱 Usar el Escáner en Movimientos</h3>
                    <p className="text-muted-foreground text-sm">
                      Esta función debe estar habilitada por un administrador en la configuración.
                    </p>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-4 py-3 border-b">
                        <span className="font-medium text-foreground text-base">Cómo escanear equipos</span>
                      </div>
                      <div className="px-4 py-3">
                        <ol className="list-decimal ml-6 space-y-2">
                          <li>Al crear un nuevo movimiento, verás el botón <strong>"Escanear"</strong></li>
                          <li>Haz clic en él para abrir la cámara del dispositivo</li>
                          <li>Apunta al código QR o de barras del equipo</li>
                          <li>El sistema identificará el equipo y lo agregará automáticamente</li>
                        </ol>
                      </div>
                    </div>

                    <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 rounded-lg">
                      <div className="flex gap-2 items-start">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-yellow-800 dark:text-yellow-200">Permisos necesarios</p>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                            Para usar el escáner necesitas el permiso "Usar escáner en Movimientos". 
                            Consulta con tu administrador si no ves el botón de escanear.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Templates Section */}
              {activeSection === 'templates' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Plantillas (Agregar Muchos Equipos a la Vez)</h2>
                  
                  <p className="text-muted-foreground">
                    Si necesitas agregar muchos equipos al sistema (por ejemplo, 100 laptops nuevas), 
                    no tienes que hacerlo uno por uno. Puedes usar un archivo de Excel para agregarlos todos de una vez.
                  </p>

                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <h3 className="font-semibold mb-2">¿Qué es una plantilla?</h3>
                    <p className="text-sm text-muted-foreground">
                      Una "plantilla" es un archivo de Excel con el formato correcto para que el sistema 
                      pueda leer los datos. Tú llenas los datos en Excel y luego los subes al sistema.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Proceso completo para agregar equipos masivamente</h3>
                    
                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                      <p className="text-sm font-medium text-emerald-400 mb-2">Resumen del proceso:</p>
                      <p className="text-sm text-muted-foreground">
                        1. Descarga la plantilla → 2. Llena los datos en Excel → 3. Sube el archivo al sistema
                      </p>
                    </div>

                    <ol className="list-decimal list-inside space-y-4 text-muted-foreground">
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 1: Ir a la página de Plantillas</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>En el menú de la izquierda, haz clic en <strong>"Plantillas"</strong></p>
                          <p>Verás una página con instrucciones y un botón para descargar</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 2: Descargar la plantilla</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Haz clic en el botón verde <strong>"Descargar Plantilla"</strong></p>
                          <p>Se descargará un archivo de Excel a tu computadora</p>
                          <p>Normalmente se guarda en la carpeta "Descargas"</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 3: Abrir el archivo en Excel</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Busca el archivo descargado (se llama algo como "Plantilla_Unidades_...")</p>
                          <p>Haz doble clic para abrirlo con Excel o Google Sheets</p>
                          <p>Verás una tabla con varias columnas</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 4: Entender las columnas</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p className="font-medium">Solo necesitas llenar 3 columnas obligatorias:</p>
                          <ul className="space-y-2 ml-4">
                            <li className="p-2 bg-red-500/10 rounded border border-red-500/20">
                              <strong>Nombre_Equipo:</strong> El nombre del tipo de equipo (ej: "MacBook Pro 14")
                            </li>
                            <li className="p-2 bg-red-500/10 rounded border border-red-500/20">
                              <strong>Número_Serie:</strong> El número de serie único de cada unidad
                            </li>
                            <li className="p-2 bg-red-500/10 rounded border border-red-500/20">
                              <strong>Ubicación:</strong> Dónde estará el equipo (ej: "Almacén")
                            </li>
                          </ul>
                          <p className="font-medium mt-3">Las demás columnas son opcionales:</p>
                          <ul className="space-y-1 ml-4 text-sm">
                            <li>• <strong>ID_Unidad:</strong> Déjala vacía, se genera automáticamente</li>
                            <li>• <strong>ID_Equipo:</strong> Déjala vacía, el sistema lo encuentra por el nombre</li>
                            <li>• <strong>Categoría:</strong> Si la dejas vacía, será "General"</li>
                            <li>• <strong>Estado:</strong> Si lo dejas vacío, será "disponible"</li>
                          </ul>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 5: Agregar tus datos</span>
                        <div className="ml-6 mt-2 space-y-2">
                          <p>Agrega una fila por cada unidad de equipo:</p>
                          <div className="overflow-x-auto">
                            <table className="text-xs border mt-2 w-full">
                              <thead>
                                <tr className="bg-muted">
                                  <th className="border p-1">ID_Unidad</th>
                                  <th className="border p-1">ID_Equipo</th>
                                  <th className="border p-1">Nombre_Equipo</th>
                                  <th className="border p-1">Número_Serie</th>
                                  <th className="border p-1">Ubicación</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="border p-1 text-muted-foreground">(vacío)</td>
                                  <td className="border p-1 text-muted-foreground">(vacío)</td>
                                  <td className="border p-1">MacBook Pro 14</td>
                                  <td className="border p-1">ABC123</td>
                                  <td className="border p-1">Almacén</td>
                                </tr>
                                <tr>
                                  <td className="border p-1 text-muted-foreground">(vacío)</td>
                                  <td className="border p-1 text-muted-foreground">(vacío)</td>
                                  <td className="border p-1">MacBook Pro 14</td>
                                  <td className="border p-1">ABC124</td>
                                  <td className="border p-1">Almacén</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <p className="text-amber-400 mt-2">⚠️ Cada número de serie debe ser diferente</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 6: Guardar el archivo</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>En Excel: Archivo → Guardar (o presiona Ctrl + S)</p>
                          <p>Asegúrate de guardarlo como archivo Excel (.xlsx)</p>
                        </div>
                      </li>
                      <li className="p-4 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 7: Subir el archivo al sistema</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>Ve a <strong>Inventario</strong> en el menú de la izquierda</p>
                          <p>Haz clic en el botón <strong>"Importar"</strong> (arriba a la derecha)</p>
                          <p>Se abrirá una ventana para seleccionar un archivo</p>
                          <p>Busca y selecciona tu archivo Excel</p>
                          <p>Haz clic en <strong>"Abrir"</strong></p>
                        </div>
                      </li>
                      <li className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <span className="font-medium text-foreground text-base">Paso 8: Esperar la confirmación</span>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>El sistema procesará el archivo (puede tardar unos segundos)</p>
                          <p>Verás un mensaje indicando cuántos equipos se agregaron</p>
                          <p className="text-emerald-400">✅ ¡Listo! Los equipos ya están en el inventario</p>
                        </div>
                      </li>
                    </ol>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-2">Problemas comunes</h3>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <p><strong>❌ "Número_Serie es requerido"</strong> - Falta el número de serie en alguna fila</p>
                          <p><strong>❌ "Serial duplicado"</strong> - Ese número de serie ya existe en el sistema</p>
                          <p><strong>❌ El equipo no aparece donde esperabas</strong> - Revisa que el nombre del equipo coincida exactamente</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-2">💡 Consejo importante</h3>
                        <p className="text-sm text-muted-foreground">
                          <strong>Antes de crear tu archivo desde cero</strong>, exporta el inventario actual. 
                          Así verás los nombres exactos de los equipos existentes y podrás copiarlos 
                          para evitar errores de escritura.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tracking Section */}
              {activeSection === 'tracking' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Rastreo de Equipos</h2>
                  
                  <p className="text-muted-foreground">
                    La sección de Rastreo te permite ver todo el equipo que está fuera del inventario,
                    ya sea en eventos o en préstamo.
                  </p>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">¿Qué puedes hacer aquí?</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Ver equipos en eventos
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Ver equipos en préstamo
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Configurar alertas de vencimiento
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Configurar alertas de bomberazos
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Users Section */}
              {activeSection === 'users' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Gestión de Usuarios</h2>
                  
                  <p className="text-muted-foreground">
                    Solo los administradores pueden crear y editar usuarios del sistema.
                  </p>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Tipos de Usuarios</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2">Super Admin</Badge>
                        <p className="text-sm text-muted-foreground">
                          Acceso total al sistema, puede crear otros super admins.
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2">Admin</Badge>
                        <p className="text-sm text-muted-foreground">
                          Acceso a la mayoría de funciones, excepto crear super admins.
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2">Supervisor</Badge>
                        <p className="text-sm text-muted-foreground">
                          Puede aprobar movimientos y ver reportes.
                        </p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <Badge className="mb-2">Usuario</Badge>
                        <p className="text-sm text-muted-foreground">
                          Acceso básico para registrar movimientos.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">🗑️ Eliminación Masiva de Usuarios</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Si tienes el permiso de "Eliminación Masiva de Usuarios", puedes seleccionar y eliminar 
                      múltiples usuarios a la vez:
                    </p>
                    <ol className="list-decimal list-inside space-y-3 text-muted-foreground">
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Encuentra la barra de selección</span>
                        <p className="ml-6 mt-1">En la parte superior de la lista de usuarios verás una barra con "Seleccionar todos"</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Selecciona los usuarios</span>
                        <p className="ml-6 mt-1">Usa los checkboxes (☐) al lado de cada usuario, o haz clic en "Seleccionar todos"</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Haz clic en "Eliminar seleccionados"</span>
                        <p className="ml-6 mt-1">Este botón rojo aparece cuando tienes usuarios seleccionados</p>
                      </li>
                      <li className="p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium text-foreground">Confirma la eliminación</span>
                        <p className="ml-6 mt-1">Deberás proporcionar una justificación (mínimo 10 caracteres)</p>
                      </li>
                    </ol>
                    <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        <strong>Nota:</strong> No puedes eliminarte a ti mismo. Tu usuario siempre aparecerá sin checkbox.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Permissions Section */}
              {activeSection === 'permissions' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Sistema de Permisos</h2>
                  
                  <p className="text-muted-foreground">
                    El sistema tiene más de 60 permisos individuales que puedes asignar a cada rol.
                  </p>

                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div>
                        <h3 className="font-semibold mb-1">Permisos Granulares</h3>
                        <p className="text-sm text-muted-foreground">
                          Puedes controlar exactamente qué puede hacer cada usuario, desde ver el dashboard
                          hasta crear movimientos sin revisión.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Settings Section */}
              {activeSection === 'settings' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Configuración</h2>
                  
                  <p className="text-muted-foreground">
                    En la sección de Estilo puedes personalizar la apariencia del sistema.
                  </p>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">¿Qué puedes personalizar?</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Colores de la aplicación
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Logo de la empresa
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Página de inicio de sesión
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        PDF de responsivas
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* FAQ Section */}
              {activeSection === 'categories' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Categorías</h2>
                  <p className="text-muted-foreground">Las categorías organizan tu inventario agrupando equipos similares.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Crear una Categoría</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Ve a "Categorías" en el menú principal</li>
                        <li>Haz clic en "Nueva Categoría"</li>
                        <li>Ingresa el nombre y una descripción opcional</li>
                        <li>Haz clic en "Crear"</li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Editar o Eliminar</h3>
                      <p className="text-sm text-muted-foreground">Usa los botones de editar o eliminar en cada fila. Los elementos eliminados van a la papelera donde puedes restaurarlos o borrarlos permanentemente.</p>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'locations' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Ubicaciones</h2>
                  <p className="text-muted-foreground">Las ubicaciones definen dónde se almacena o se asigna el equipo físicamente.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Gestión de Ubicaciones</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Ve a "Ubicaciones" en el menú principal</li>
                        <li>Crea nuevas ubicaciones con nombre y descripción</li>
                        <li>Cada ubicación lleva un conteo automático de equipos asignados</li>
                        <li>Al registrar movimientos, selecciona la ubicación de origen/destino</li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Stock por Ubicación</h3>
                      <p className="text-sm text-muted-foreground">En el detalle de cada equipo puedes ver cuántas unidades hay en cada ubicación (disponibles, asignadas, con fallas).</p>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'responsibles' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Responsables</h2>
                  <p className="text-muted-foreground">Los responsables son las personas que reciben equipo en préstamo o salida.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Crear Responsable</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Ve a "Responsables" en el menú principal</li>
                        <li>Haz clic en "Nuevo Responsable"</li>
                        <li>Ingresa nombre, email, departamento y opcionalmente una contraseña</li>
                        <li>La contraseña se usa para verificar la identidad al firmar responsivas</li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Contraseña de Responsable</h3>
                      <p className="text-sm text-muted-foreground">Si el responsable tiene contraseña, se le pedirá al momento de registrar una salida. Si no tiene, se usará la contraseña del usuario vinculado. Puedes configurar o cambiar la contraseña desde el botón de editar.</p>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'calendar' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Calendario</h2>
                  <p className="text-muted-foreground">Vista de calendario con todos los movimientos y eventos del sistema.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Navegación</h3>
                      <p className="text-sm text-muted-foreground">Puedes cambiar entre vista de mes, semana o día. Los movimientos se muestran codificados por color: verde para salidas, azul para retornos, naranja para préstamos.</p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Alertas de Vencimiento</h3>
                      <p className="text-sm text-muted-foreground">Los préstamos próximos a vencer se resaltan automáticamente. El sistema envía notificaciones cuando un préstamo está por vencer o ya venció.</p>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'trash' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Papelera</h2>
                  <p className="text-muted-foreground">El sistema usa eliminación suave (soft-delete) para proteger tus datos.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Funcionamiento</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li>Al eliminar un elemento, se mueve a la papelera (no se borra)</li>
                        <li>Cada sección tiene su propia papelera: Inventario, Categorías, Ubicaciones, Usuarios, Responsables</li>
                        <li>Cambia a la pestaña "Papelera" para ver los elementos eliminados</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Opciones en la Papelera</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li><strong>Restaurar:</strong> Devuelve el elemento a su estado activo</li>
                        <li><strong>Eliminar Permanentemente:</strong> Borra el elemento definitivamente (no se puede deshacer)</li>
                        <li><strong>Vaciar Papelera:</strong> Elimina permanentemente todos los elementos de la papelera</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'announcements' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Anuncios</h2>
                  <p className="text-muted-foreground">Envía comunicados importantes a todos los usuarios del sistema.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Crear un Anuncio</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Ve a "Anuncios" en el menú de administración</li>
                        <li>Haz clic en "Nuevo Anuncio"</li>
                        <li>Escribe el título y contenido del mensaje</li>
                        <li>Al publicar, el anuncio aparecerá como modal al iniciar sesión y como notificación en la campana</li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Tipos de Anuncios</h3>
                      <p className="text-sm text-muted-foreground">Puedes crear anuncios informativos, de advertencia o urgentes. Los anuncios urgentes se resaltan visualmente para captar la atención del usuario.</p>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'backup' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Backup y Restauración</h2>
                  <p className="text-muted-foreground">Protege los datos del sistema con respaldos automáticos y manuales.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Backup Manual</h3>
                      <p className="text-sm text-muted-foreground">Ve a "Backup" y haz clic en "Crear Backup". Se genera un archivo con toda la base de datos que puedes descargar localmente.</p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Backup Automático con OneDrive</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Vincula tu cuenta de Microsoft/OneDrive en la sección de Backup</li>
                        <li>Configura la frecuencia de backup automático (diario, cada 6h, etc.)</li>
                        <li>Los backups se suben automáticamente a tu OneDrive</li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Restaurar desde Backup</h3>
                      <p className="text-sm text-muted-foreground">Puedes restaurar desde un archivo local o desde OneDrive. Ten cuidado: restaurar reemplaza todos los datos actuales con los del backup.</p>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'maintenance' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Mantenimiento de Equipos</h2>
                  <p className="text-muted-foreground">Gestiona el mantenimiento preventivo y correctivo de tus equipos.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Crear Registro de Mantenimiento</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Ve al menú <strong>Mantenimiento</strong> en la barra lateral</li>
                        <li>Haz clic en <strong>"Nuevo Mantenimiento"</strong></li>
                        <li>Selecciona el equipo y opcionalmente la unidad específica</li>
                        <li>Elige el tipo, estado y prioridad</li>
                        <li>Completa la descripción y el técnico asignado</li>
                        <li>Haz clic en <strong>"Crear"</strong></li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Tipos de Mantenimiento</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li><strong>Preventivo:</strong> Mantenimiento programado para prevenir fallas</li>
                        <li><strong>Correctivo:</strong> Reparación cuando algo ya falló</li>
                        <li><strong>Calibración:</strong> Ajuste de precisión del equipo</li>
                        <li><strong>Inspección:</strong> Revisión visual o funcional</li>
                        <li><strong>Limpieza:</strong> Limpieza del equipo</li>
                        <li><strong>Actualización / Upgrade:</strong> Mejora de software o hardware</li>
                        <li><strong>Reparación:</strong> Reparación de componentes dañados</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Estados del Mantenimiento</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li><strong>Programado:</strong> El mantenimiento está agendado pero no ha iniciado</li>
                        <li><strong>Aprobado:</strong> Se aprobó la solicitud de mantenimiento</li>
                        <li><strong>En Progreso:</strong> El técnico está trabajando en el equipo</li>
                        <li><strong>Esperando Piezas:</strong> Se requieren refacciones para continuar</li>
                        <li><strong>En Revisión:</strong> Se está verificando el trabajo realizado</li>
                        <li><strong>Completado:</strong> El mantenimiento se terminó exitosamente</li>
                        <li><strong>Cancelado:</strong> Se canceló el mantenimiento</li>
                        <li><strong>Rechazado:</strong> La solicitud fue rechazada</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Prioridades</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li><strong>Baja:</strong> Puede esperar, sin impacto en operaciones</li>
                        <li><strong>Normal:</strong> Debe atenderse en tiempo estándar</li>
                        <li><strong>Alta:</strong> Requiere atención pronta</li>
                        <li><strong>Urgente:</strong> Requiere atención inmediata, equipo crítico</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-lg bg-blue-500/5">
                      <h3 className="font-semibold mb-2 flex items-center gap-2"><Info className="h-4 w-4 text-blue-400" /> Estado Automático de Unidades</h3>
                      <p className="text-sm text-muted-foreground">
                        Cuando un mantenimiento pasa a <strong>En Progreso</strong>, <strong>Esperando Piezas</strong> o <strong>En Revisión</strong>, la unidad del equipo cambia automáticamente a "en mantenimiento". Al completar, cancelar o rechazar, la unidad vuelve a "disponible".
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Filtros y Búsqueda</h3>
                      <p className="text-sm text-muted-foreground">
                        Utiliza los filtros por tipo, estado y equipo para encontrar registros rápidamente. También puedes buscar por descripción o nombre de técnico.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'reservations' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Reservas de Equipo</h2>
                  <p className="text-muted-foreground">Reserva equipos para fechas futuras y evita conflictos de disponibilidad.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Crear una Reserva</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Ve al menú <strong>Reservas</strong> en la barra lateral</li>
                        <li>Haz clic en <strong>"Nueva Reserva"</strong></li>
                        <li>Selecciona el equipo, fecha de inicio y fecha de fin</li>
                        <li>Escribe el propósito de la reserva</li>
                        <li>El sistema verifica automáticamente que no haya conflictos con otras reservas</li>
                        <li>Haz clic en <strong>"Crear"</strong></li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg bg-yellow-500/5">
                      <h3 className="font-semibold mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-yellow-400" /> Detección de Conflictos</h3>
                      <p className="text-sm text-muted-foreground">
                        Si el equipo ya tiene una reserva activa en las fechas seleccionadas, el sistema te avisará del conflicto y no permitirá crear la reserva duplicada.
                      </p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Estados de Reserva</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li><strong>Activa:</strong> La reserva está vigente</li>
                        <li><strong>Completada:</strong> El equipo fue devuelto</li>
                        <li><strong>Cancelada:</strong> La reserva fue cancelada</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'clients' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Clientes</h2>
                  <p className="text-muted-foreground">Gestiona los datos de tus clientes y su historial de eventos.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Crear un Cliente</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Ve a <strong>Clientes</strong> desde el menu lateral</li>
                        <li>Haz clic en <strong>"+ Nuevo Cliente"</strong></li>
                        <li>Llena los campos: Nombre (obligatorio), Empresa, Contacto, Email, Telefono, Direccion</li>
                        <li>Haz clic en <strong>"Crear Cliente"</strong></li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Vincular Cliente a Movimiento</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Al crear una salida tipo Evento, debajo de los campos de cliente veras un selector</li>
                        <li>Selecciona un cliente registrado para pre-rellenar nombre y contacto</li>
                        <li>Los campos siguen siendo editables si necesitas ajustarlos</li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <h3 className="font-semibold mb-2">Historial de Eventos</h3>
                      <p className="text-sm text-muted-foreground">Haz clic en el icono del ojo en cualquier cliente para ver todo su historial: movimientos, equipos, fechas, responsables y condicion del equipo.</p>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'scheduled_reports' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Reportes Programados</h2>
                  <p className="text-muted-foreground">Configura reportes automáticos que se generan periódicamente.</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Configurar un Reporte</h3>
                      <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Ve al menú <strong>Reportes Programados</strong></li>
                        <li>Selecciona el tipo de reporte (inventario, movimientos, etc.)</li>
                        <li>Configura la frecuencia (diario, semanal, mensual)</li>
                        <li>Agrega los destinatarios del reporte</li>
                        <li>Activa o desactiva el reporte según necesites</li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">Tipos de Reporte</h3>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li><strong>Inventario:</strong> Resumen completo del estado del inventario</li>
                        <li><strong>Movimientos:</strong> Entradas y salidas del período</li>
                        <li><strong>Mantenimiento:</strong> Estado de mantenimientos pendientes y completados</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'faq' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold">Preguntas Frecuentes</h2>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">¿Qué hago si olvidé mi contraseña?</h3>
                      <p className="text-sm text-muted-foreground">
                        Haz clic en "¿Olvidaste tu contraseña?" en la pantalla de inicio de sesión
                        y sigue las instrucciones.
                      </p>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">¿Cómo imprimo una responsiva?</h3>
                      <p className="text-sm text-muted-foreground">
                        En la lista de movimientos, haz clic en el ícono de PDF junto al movimiento
                        que deseas imprimir.
                      </p>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">¿Qué es un "bomberazo"?</h3>
                      <p className="text-sm text-muted-foreground">
                        Es cuando un equipo sale sin revisión previa debido a una emergencia.
                        El sistema registra estas salidas para seguimiento.
                      </p>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">¿Puedo ver quién hizo un cambio?</h3>
                      <p className="text-sm text-muted-foreground">
                        Sí, en la sección de Auditoría puede ver todos los cambios realizados
                        en el sistema y quién los hizo.
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">🗑️ ¿Cómo elimino múltiples elementos a la vez?</h3>
                      <p className="text-sm text-muted-foreground">
                        La función de "Eliminación Masiva" está disponible para administradores en las páginas de:
                      </p>
                      <ul className="text-sm text-muted-foreground mt-2 ml-4 space-y-1">
                        <li>• <strong>Usuarios:</strong> Selecciona múltiples usuarios (excepto el tuyo) y elimínalos</li>
                        <li>• <strong>Categorías:</strong> Elimina múltiples categorías a la vez</li>
                        <li>• <strong>Ubicaciones:</strong> Elimina múltiples ubicaciones a la vez</li>
                        <li>• <strong>Responsables:</strong> Elimina múltiples responsables a la vez</li>
                        <li>• <strong>Inventario:</strong> Elimina múltiples equipos a la vez</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-2">
                        Solo verás esta opción si tienes el permiso correspondiente de "Eliminación Masiva".
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">📊 ¿Se registra mi actividad en el sistema?</h3>
                      <p className="text-sm text-muted-foreground">
                        Sí, el sistema registra automáticamente todas las acciones que realizas:
                      </p>
                      <ul className="text-sm text-muted-foreground mt-2 ml-4 space-y-1">
                        <li>• Páginas que visitas</li>
                        <li>• Botones que presionas</li>
                        <li>• Creación, edición y eliminación de datos</li>
                        <li>• Inicio y cierre de sesión</li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-2">
                        Esto ayuda a los administradores a mantener la seguridad del sistema.
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">🏷️ ¿Para qué sirven las etiquetas QR?</h3>
                      <p className="text-sm text-muted-foreground">
                        Las etiquetas QR y códigos de barras te permiten:
                      </p>
                      <ul className="text-sm text-muted-foreground mt-2 ml-4 space-y-1">
                        <li>• Identificar equipos rápidamente escaneando con el celular</li>
                        <li>• Registrar salidas/entradas escaneando el código</li>
                        <li>• Imprimir etiquetas para pegar en los equipos</li>
                      </ul>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h3 className="font-semibold mb-2">👁️ ¿Cómo veo una responsiva sin descargarla?</h3>
                      <p className="text-sm text-muted-foreground">
                        En la página de Responsivas, haz clic en el botón del ojo (👁️) junto a cualquier 
                        responsiva para abrirla en una nueva pestaña sin necesidad de descargarla.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserManualPage;
